// Mock data for Client File Management - will be replaced with real API when backend is ready
import type {
  Client,
  Folder,
  FolderContents,
  ClientDocument,
  ClientStats,
  FolderStats,
  FolderBreadcrumb,
  ClientType,
  ClientStatus,
  FolderType,
  ClientListFilters,
  ClientListResponse,
  CreateClientRequest,
  UpdateClientRequest,
  CreateFolderRequest,
  UpdateFolderRequest,
  WorkHistoryItem,
  Deadline,
  DeadlineType,
  DeadlineStatus,
  DeadlineListFilters,
  DeadlineListResponse,
  DeadlineStats,
  CreateDeadlineRequest,
  UpdateDeadlineRequest,
  GlobalSearchResult,
  GlobalSearchResponse,
} from '@/types/client';

// Generate unique IDs
let clientIdCounter = 100;
let folderIdCounter = 100;
let documentIdCounter = 100;

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Sample client data for accounting firm
const sampleClientsData: Array<{
  clientCode: string;
  displayName: string;
  clientType: ClientType;
  email: string;
  phone: string;
  abn?: string;
  industry: string;
  serviceDescription: string;
  workHistory: WorkHistoryItem[];
}> = [
  {
    clientCode: 'ACM001',
    displayName: 'Acme Corporation Pty Ltd',
    clientType: 'company',
    email: 'accounts@acmecorp.com.au',
    phone: '02 9876 5432',
    abn: '12 345 678 901',
    industry: 'Manufacturing',
    serviceDescription: 'We provide full-service accounting for Acme Corporation including annual financial statement preparation, corporate tax returns, monthly BAS lodgements, and payroll processing for their 45 employees. The company has complex inventory tracking requirements and international supplier payments that require careful GST treatment. We also assist with their R&D tax incentive claims annually.',
    workHistory: [
      { date: '2024-11-15', description: 'Lodged Q2 BAS - GST refund of $12,450', type: 'bas_lodgement' },
      { date: '2024-10-28', description: 'FY2024 Company Tax Return lodged', type: 'tax_lodgement' },
      { date: '2024-09-20', description: 'Annual review meeting with CFO John Smith', type: 'meeting' },
      { date: '2024-08-15', description: 'Lodged Q1 BAS', type: 'bas_lodgement' },
      { date: '2024-06-30', description: 'FY2024 Financial Statements completed', type: 'review' },
    ]
  },
  {
    clientCode: 'JFT002',
    displayName: 'Johnson Family Trust',
    clientType: 'trust',
    email: 'mary.johnson@email.com',
    phone: '0412 345 678',
    industry: 'Property Investment',
    serviceDescription: 'The Johnson Family Trust holds a portfolio of 6 residential investment properties across Sydney. We prepare the annual trust tax return, manage rental income reconciliation, track depreciation schedules for each property, and advise on distribution strategies to minimise family tax. Mary Johnson is the primary contact and sole trustee.',
    workHistory: [
      { date: '2024-10-31', description: 'FY2024 Trust Tax Return lodged', type: 'tax_lodgement' },
      { date: '2024-09-15', description: 'Distribution minutes prepared for FY2024', type: 'other' },
      { date: '2024-07-20', description: 'New property acquisition - updated depreciation schedule', type: 'review' },
      { date: '2024-05-10', description: 'Meeting re: property sale CGT implications', type: 'meeting' },
      { date: '2023-10-30', description: 'FY2023 Trust Tax Return lodged', type: 'tax_lodgement' },
    ]
  },
  {
    clientCode: 'SAS003',
    displayName: 'Smith & Associates',
    clientType: 'partnership',
    email: 'admin@smithassoc.com.au',
    phone: '02 8765 4321',
    abn: '23 456 789 012',
    industry: 'Legal Services',
    serviceDescription: 'Smith & Associates is a mid-size law firm with 3 partners. We handle the partnership tax return, individual partner returns, quarterly BAS, and monthly bookkeeping. The firm has a trust account requiring special reconciliation procedures. Partners draw variable amounts monthly based on WIP recovery, requiring careful profit allocation calculations.',
    workHistory: [
      { date: '2024-11-20', description: 'October BAS lodged', type: 'bas_lodgement' },
      { date: '2024-10-25', description: 'FY2024 Partnership Return and 3 Partner Returns lodged', type: 'tax_lodgement' },
      { date: '2024-09-05', description: 'Trust account annual reconciliation completed', type: 'audit' },
      { date: '2024-08-20', description: 'September BAS lodged', type: 'bas_lodgement' },
      { date: '2024-07-15', description: 'Partner profit distribution review meeting', type: 'meeting' },
    ]
  },
  {
    clientCode: 'BWE004',
    displayName: 'Brown Williams Estate',
    clientType: 'trust',
    email: 'estate@bwestate.com',
    phone: '03 9123 4567',
    industry: 'Estate Management',
    serviceDescription: 'We manage the deceased estate of Harold Brown Williams, including ongoing trust administration, beneficiary distributions, and investment income reporting. The estate holds a share portfolio, term deposits, and one commercial property. Annual trustee meetings are held with the executor and three adult beneficiaries to review distributions.',
    workHistory: [
      { date: '2024-10-28', description: 'FY2024 Deceased Estate Return lodged', type: 'tax_lodgement' },
      { date: '2024-09-10', description: 'Annual beneficiary meeting - approved distributions', type: 'meeting' },
      { date: '2024-06-15', description: 'Dividend income summary prepared for FY2024', type: 'review' },
      { date: '2024-03-20', description: 'Correspondence with ATO re: estate finalisation timeline', type: 'correspondence' },
      { date: '2023-10-30', description: 'FY2023 Deceased Estate Return lodged', type: 'tax_lodgement' },
    ]
  },
  {
    clientCode: 'TSI005',
    displayName: 'TechStart Industries',
    clientType: 'company',
    email: 'finance@techstart.io',
    phone: '02 9111 2222',
    abn: '34 567 890 123',
    industry: 'Technology',
    serviceDescription: 'TechStart is a growing SaaS company with subscription revenue requiring deferred income tracking. We provide monthly management accounts, quarterly BAS, annual tax returns, and advisory on employee share scheme (ESS) tax implications. They have 12 employees with complex salary packaging arrangements including laptop FBT.',
    workHistory: [
      { date: '2024-11-18', description: 'Q2 BAS lodged - $8,200 payable', type: 'bas_lodgement' },
      { date: '2024-10-20', description: 'FY2024 Company Tax Return lodged', type: 'tax_lodgement' },
      { date: '2024-10-05', description: 'ESS reporting for 3 new option grants', type: 'review' },
      { date: '2024-09-25', description: 'Meeting re: Series A funding tax implications', type: 'meeting' },
      { date: '2024-08-15', description: 'Q1 BAS lodged', type: 'bas_lodgement' },
    ]
  },
  {
    clientCode: 'DBR006',
    displayName: 'David Brown',
    clientType: 'individual',
    email: 'dbrown@gmail.com',
    phone: '0423 456 789',
    industry: 'Sole Trader - Consulting',
    serviceDescription: 'David operates as a sole trader IT consultant working primarily from home. We prepare his annual individual tax return including business schedule, track motor vehicle and home office expenses, and manage quarterly PAYG instalments. He has a simple structure but high income requiring careful deduction documentation.',
    workHistory: [
      { date: '2024-10-15', description: 'FY2024 Individual Tax Return lodged - refund $3,200', type: 'tax_lodgement' },
      { date: '2024-09-01', description: 'Phone consultation re: new client contract structure', type: 'meeting' },
      { date: '2024-07-28', description: 'Q4 PAYG instalment lodged', type: 'bas_lodgement' },
      { date: '2024-04-28', description: 'Q3 PAYG instalment lodged', type: 'bas_lodgement' },
      { date: '2023-10-20', description: 'FY2023 Individual Tax Return lodged', type: 'tax_lodgement' },
    ]
  },
  {
    clientCode: 'GVP007',
    displayName: 'Global Ventures Partnership',
    clientType: 'partnership',
    email: 'partners@globalventures.com',
    phone: '02 9333 4444',
    abn: '45 678 901 234',
    industry: 'Investment',
    serviceDescription: 'Global Ventures is an investment partnership with 5 partners holding shares in private companies and listed securities. We manage complex CGT calculations, foreign income reporting, and partner distribution statements. The partnership frequently trades securities requiring detailed cost base tracking and CGT discount eligibility assessment.',
    workHistory: [
      { date: '2024-11-01', description: 'FY2024 Partnership Return and 5 Partner statements lodged', type: 'tax_lodgement' },
      { date: '2024-10-10', description: 'CGT schedule prepared - 23 disposals in FY2024', type: 'review' },
      { date: '2024-08-15', description: 'Foreign income reconciliation for US dividends', type: 'review' },
      { date: '2024-07-20', description: 'Partner meeting re: new investment structure', type: 'meeting' },
      { date: '2024-05-30', description: 'Correspondence with ATO re: foreign tax offset query', type: 'correspondence' },
    ]
  },
  {
    clientCode: 'MNF008',
    displayName: 'Miller Nonprofit Foundation',
    clientType: 'company',
    email: 'treasurer@millerfoundation.org',
    phone: '03 8444 5555',
    abn: '56 789 012 345',
    industry: 'Not-for-Profit',
    serviceDescription: 'The Miller Foundation is a DGR-registered charity focused on youth education. We assist with annual ACNC reporting, income tax exemption compliance, FBT exemption management for staff, and grant acquittal reports. The foundation receives approximately $500k in donations annually and employs 8 part-time staff.',
    workHistory: [
      { date: '2024-11-30', description: 'ACNC Annual Information Statement lodged', type: 'other' },
      { date: '2024-10-15', description: 'FY2024 Income Tax Return (exempt) lodged', type: 'tax_lodgement' },
      { date: '2024-09-20', description: 'Grant acquittal report prepared for State Government', type: 'review' },
      { date: '2024-08-10', description: 'Board meeting presentation on financial position', type: 'meeting' },
      { date: '2024-03-31', description: 'FBT Return lodged - nil liability (exempt)', type: 'tax_lodgement' },
    ]
  },
  {
    clientCode: 'AMG009',
    displayName: 'Anderson Manufacturing Group',
    clientType: 'company',
    email: 'cfo@andersonmfg.com.au',
    phone: '02 9555 6666',
    abn: '67 890 123 456',
    industry: 'Manufacturing',
    serviceDescription: 'Anderson Manufacturing is a family-owned business with $5M+ annual revenue manufacturing industrial equipment. We provide full outsourced finance function including monthly accounts, inventory costing, BAS, payroll for 25 staff, and annual audit preparation. The company has plant and equipment requiring detailed depreciation tracking.',
    workHistory: [
      { date: '2024-11-25', description: 'October BAS lodged - GST payable $18,500', type: 'bas_lodgement' },
      { date: '2024-10-30', description: 'FY2024 Company Tax Return lodged', type: 'tax_lodgement' },
      { date: '2024-10-01', description: 'External audit fieldwork completed', type: 'audit' },
      { date: '2024-09-15', description: 'Monthly management accounts - August', type: 'review' },
      { date: '2024-08-20', description: 'CFO meeting re: equipment financing options', type: 'meeting' },
    ]
  },
  {
    clientCode: 'TMG010',
    displayName: 'Taylor Medical Group',
    clientType: 'company',
    email: 'admin@taylormedical.com.au',
    phone: '02 9666 7777',
    abn: '78 901 234 567',
    industry: 'Healthcare',
    serviceDescription: 'Taylor Medical is a GP clinic with 4 doctors and 6 support staff. We manage medical practice accounting including Medicare bulk billing reconciliation, practitioner service fees, BAS, payroll, and annual returns. The practice has complex income splitting arrangements between the company and practitioner service trusts.',
    workHistory: [
      { date: '2024-11-20', description: 'October BAS lodged', type: 'bas_lodgement' },
      { date: '2024-10-25', description: 'FY2024 Company Return and 4 Service Trust Returns lodged', type: 'tax_lodgement' },
      { date: '2024-09-30', description: 'Medicare reconciliation for FY2024 completed', type: 'review' },
      { date: '2024-08-15', description: 'Meeting with practice manager re: new doctor onboarding', type: 'meeting' },
      { date: '2024-07-20', description: 'Superannuation guarantee audit completed', type: 'audit' },
    ]
  },
  {
    clientCode: 'GFT011',
    displayName: 'Garcia Family Trust',
    clientType: 'trust',
    email: 'carlos.garcia@email.com',
    phone: '0434 567 890',
    industry: 'Family Trust',
    serviceDescription: 'The Garcia Family Trust is a discretionary trust used for asset protection and income splitting for the Garcia family business. We prepare annual trust returns, distribution resolutions, and advise on streaming of franked dividends and capital gains to appropriate beneficiaries including adult children.',
    workHistory: [
      { date: '2024-10-28', description: 'FY2024 Trust Tax Return lodged', type: 'tax_lodgement' },
      { date: '2024-09-01', description: 'Distribution resolution and minutes prepared', type: 'other' },
      { date: '2024-07-15', description: 'Family meeting re: trust restructure options', type: 'meeting' },
      { date: '2024-05-20', description: 'Franking credit analysis for streaming strategy', type: 'review' },
      { date: '2023-10-25', description: 'FY2023 Trust Tax Return lodged', type: 'tax_lodgement' },
    ]
  },
  {
    clientCode: 'WCS012',
    displayName: 'Wilson Consulting Services',
    clientType: 'company',
    email: 'info@wilsonconsulting.com.au',
    phone: '02 9777 8888',
    abn: '89 012 345 678',
    industry: 'Consulting',
    serviceDescription: 'Wilson Consulting provides management consulting services to corporate clients. We handle their company tax return, director loan account monitoring, quarterly BAS, and personal returns for the two director shareholders. The company has regular overseas travel requiring detailed FBT and GST analysis.',
    workHistory: [
      { date: '2024-11-15', description: 'Q2 BAS lodged', type: 'bas_lodgement' },
      { date: '2024-10-22', description: 'FY2024 Company Return and 2 Individual Returns lodged', type: 'tax_lodgement' },
      { date: '2024-09-10', description: 'Director loan account reconciliation completed', type: 'review' },
      { date: '2024-08-05', description: 'Meeting re: international expansion tax implications', type: 'meeting' },
      { date: '2024-03-28', description: 'FBT Return lodged - $2,400 liability', type: 'tax_lodgement' },
    ]
  },
  {
    clientCode: 'SMS013',
    displayName: 'Smith SMSF',
    clientType: 'smsf',
    email: 'smsf@smithfamily.com',
    phone: '0445 678 901',
    industry: 'Superannuation',
    serviceDescription: 'The Smith Family SMSF has 2 members (husband and wife) with $1.2M in assets including listed shares, managed funds, and a commercial property under LRBA. We prepare annual financial statements, tax return, and audit coordination. The fund is in accumulation phase with regular concessional contributions from both members.',
    workHistory: [
      { date: '2024-11-28', description: 'FY2024 SMSF Annual Return lodged', type: 'tax_lodgement' },
      { date: '2024-11-15', description: 'SMSF Audit completed - unqualified opinion', type: 'audit' },
      { date: '2024-10-20', description: 'FY2024 Financial Statements finalised', type: 'review' },
      { date: '2024-09-05', description: 'Meeting re: transition to pension phase strategy', type: 'meeting' },
      { date: '2024-07-15', description: 'LRBA loan reconciliation and compliance check', type: 'review' },
    ]
  },
  {
    clientCode: 'RET014',
    displayName: 'Riverside Enterprises Trust',
    clientType: 'trust',
    email: 'trustee@riverside.com.au',
    phone: '03 9888 9999',
    industry: 'Commercial Property',
    serviceDescription: 'Riverside Enterprises Trust owns a commercial office building leased to 4 tenants. We manage annual trust returns, GST on commercial rent, depreciation schedules, and building capital works deductions. The trust also handles tenant outgoings reconciliations and manages a sinking fund for future maintenance.',
    workHistory: [
      { date: '2024-11-05', description: 'FY2024 Trust Tax Return lodged', type: 'tax_lodgement' },
      { date: '2024-10-15', description: 'Q1 BAS lodged - GST on rent received', type: 'bas_lodgement' },
      { date: '2024-09-20', description: 'Tenant outgoings reconciliation completed', type: 'review' },
      { date: '2024-08-10', description: 'Meeting with property manager re: new lease terms', type: 'meeting' },
      { date: '2024-06-30', description: 'Depreciation schedule updated for building improvements', type: 'review' },
    ]
  },
  {
    clientCode: 'JWL015',
    displayName: 'Jane Wilson',
    clientType: 'individual',
    email: 'jane.wilson@outlook.com',
    phone: '0456 789 012',
    industry: 'Freelance Designer',
    serviceDescription: 'Jane is a freelance graphic designer working from her home studio. We prepare her individual tax return with business schedule, manage quarterly BAS as she is GST registered, and track her home office and equipment expenses. Jane has a mix of Australian and overseas clients requiring careful income sourcing.',
    workHistory: [
      { date: '2024-10-18', description: 'FY2024 Individual Tax Return lodged - $1,850 payable', type: 'tax_lodgement' },
      { date: '2024-10-28', description: 'Q1 BAS lodged', type: 'bas_lodgement' },
      { date: '2024-08-20', description: 'Phone call re: new equipment purchase deductibility', type: 'meeting' },
      { date: '2024-07-28', description: 'Q4 BAS lodged', type: 'bas_lodgement' },
      { date: '2024-04-28', description: 'Q3 BAS lodged', type: 'bas_lodgement' },
    ]
  },
];

// Standard folder structure for each client
const standardFolders: Array<{ name: string; folderType: FolderType }> = [
  { name: 'Tax Returns', folderType: 'tax_returns' },
  { name: 'BAS Statements', folderType: 'bas' },
  { name: 'Financial Statements', folderType: 'financials' },
  { name: 'Correspondence', folderType: 'correspondence' },
  { name: 'Working Papers', folderType: 'workpapers' },
  { name: 'General Documents', folderType: 'general' },
];

// Sample document names
const sampleDocuments = [
  { name: '2024_Income_Tax_Return.pdf', type: 'application/pdf', size: 2456789 },
  { name: '2023_Income_Tax_Return.pdf', type: 'application/pdf', size: 1987654 },
  { name: 'Q4_2024_BAS.pdf', type: 'application/pdf', size: 345678 },
  { name: 'Q3_2024_BAS.pdf', type: 'application/pdf', size: 312456 },
  { name: 'Financial_Statements_FY2024.xlsx', type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', size: 567890 },
  { name: 'Balance_Sheet_2024.xlsx', type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', size: 234567 },
  { name: 'Engagement_Letter.pdf', type: 'application/pdf', size: 123456 },
  { name: 'ATO_Correspondence_Nov2024.pdf', type: 'application/pdf', size: 89012 },
  { name: 'Bank_Reconciliation_Dec2024.xlsx', type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', size: 156789 },
  { name: 'Receipt_Office_Supplies.jpg', type: 'image/jpeg', size: 45678 },
];

// In-memory data stores
export const mockClients: Client[] = [];
export const mockFolders: Folder[] = [];
export const mockDeadlines: Deadline[] = [];

// Initialize mock data
function initializeMockData() {
  const tenantId = 'tenant-001';
  const now = new Date().toISOString();

  sampleClientsData.forEach((data, index) => {
    const clientId = generateId();
    const createdDate = new Date(Date.now() - (365 - index * 25) * 24 * 60 * 60 * 1000).toISOString();

    // Calculate document counts and sizes
    let totalDocs = 0;
    let totalSize = 0;
    let indexedDocs = 0;

    // Create folders and calculate totals
    const clientFolders: Folder[] = [];

    standardFolders.forEach((folderData, folderIndex) => {
      const folderId = generateId();
      const docCount = Math.floor(Math.random() * 12) + 3;
      const sizeBytes = docCount * (Math.floor(Math.random() * 400000) + 100000);
      const indexed = Math.floor(docCount * 0.8);

      totalDocs += docCount;
      totalSize += sizeBytes;
      indexedDocs += indexed;

      const folder: Folder = {
        id: folderId,
        tenantId,
        clientId,
        parentFolderId: undefined,
        name: folderData.name,
        fullPath: `/${data.clientCode}/${folderData.name}`,
        s3Prefix: `clients/${clientId}/${folderId}/`,
        folderType: folderData.folderType,
        documentCount: docCount,
        subfolderCount: 0,
        totalSizeBytes: sizeBytes,
        createdAt: createdDate,
        updatedAt: now,
      };
      clientFolders.push(folder);
      mockFolders.push(folder);

      // Add year subfolders for Tax Returns
      if (folderData.name === 'Tax Returns') {
        ['FY2024', 'FY2023', 'FY2022'].forEach((year, yi) => {
          const subFolderId = generateId();
          const subDocCount = Math.floor(Math.random() * 5) + 1;
          const subSize = subDocCount * (Math.floor(Math.random() * 300000) + 50000);

          folder.subfolderCount++;

          mockFolders.push({
            id: subFolderId,
            tenantId,
            clientId,
            parentFolderId: folderId,
            name: year,
            fullPath: `/${data.clientCode}/${folderData.name}/${year}`,
            s3Prefix: `clients/${clientId}/${folderId}/${subFolderId}/`,
            folderType: 'tax_returns',
            documentCount: subDocCount,
            subfolderCount: 0,
            totalSizeBytes: subSize,
            createdAt: createdDate,
            updatedAt: now,
          });
        });
      }

      // Add quarterly subfolders for BAS
      if (folderData.name === 'BAS Statements') {
        ['Q4 2024', 'Q3 2024', 'Q2 2024', 'Q1 2024'].forEach((quarter) => {
          const subFolderId = generateId();
          const subDocCount = Math.floor(Math.random() * 3) + 1;
          const subSize = subDocCount * (Math.floor(Math.random() * 150000) + 30000);

          folder.subfolderCount++;

          mockFolders.push({
            id: subFolderId,
            tenantId,
            clientId,
            parentFolderId: folderId,
            name: quarter,
            fullPath: `/${data.clientCode}/${folderData.name}/${quarter}`,
            s3Prefix: `clients/${clientId}/${folderId}/${subFolderId}/`,
            folderType: 'bas',
            documentCount: subDocCount,
            subfolderCount: 0,
            totalSizeBytes: subSize,
            createdAt: createdDate,
            updatedAt: now,
          });
        });
      }
    });

    const client: Client = {
      id: clientId,
      tenantId,
      clientCode: data.clientCode,
      displayName: data.displayName,
      clientType: data.clientType,
      email: data.email,
      phone: data.phone,
      address: `${100 + index} Business Street, Suite ${index + 1}, Sydney NSW 2000`,
      abn: data.abn,
      industry: data.industry,
      totalDocuments: totalDocs,
      totalSizeBytes: totalSize,
      indexedDocuments: indexedDocs,
      serviceDescription: data.serviceDescription,
      workHistory: data.workHistory,
      aiSummary: `${data.displayName} is a ${data.industry.toLowerCase()} client with ${totalDocs} documents on file. Last tax return lodged for FY2024.`,
      tags: index % 2 === 0 ? ['Priority', 'Annual Review'] : ['Standard'],
      status: index % 7 === 0 ? 'inactive' : 'active',
      createdAt: createdDate,
      updatedAt: now,
    };

    mockClients.push(client);
  });

  // Initialize mock deadlines after clients are created
  initializeDeadlines(tenantId);
}

// Initialize deadlines for clients
function initializeDeadlines(tenantId: string) {
  const now = new Date();

  // Helper to calculate deadline status based on due date
  const getStatus = (dueDate: Date, completed: boolean): DeadlineStatus => {
    if (completed) return 'completed';
    const daysUntil = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return 'overdue';
    if (daysUntil <= 7) return 'due_soon';
    return 'upcoming';
  };

  // Sample deadlines data - will be generated for each relevant client
  const deadlineTemplates: Array<{
    title: string;
    deadlineType: DeadlineType;
    description: string;
    recurrence: 'none' | 'monthly' | 'quarterly' | 'annually';
    daysFromNow: number;
    completed?: boolean;
    lodgementReference?: string;
  }> = [
    // BAS deadlines
    { title: 'Q2 FY2025 BAS', deadlineType: 'bas', description: 'Business Activity Statement for October-December 2024', recurrence: 'quarterly', daysFromNow: 14, completed: false },
    { title: 'Q1 FY2025 BAS', deadlineType: 'bas', description: 'Business Activity Statement for July-September 2024', recurrence: 'quarterly', daysFromNow: -20, completed: true, lodgementReference: 'BAS-2024-Q1-12345' },

    // Income Tax deadlines
    { title: 'FY2024 Income Tax Return', deadlineType: 'income_tax', description: 'Annual income tax return for financial year 2023-24', recurrence: 'annually', daysFromNow: -45, completed: true, lodgementReference: 'ITR-2024-67890' },
    { title: 'FY2025 Income Tax Return', deadlineType: 'income_tax', description: 'Annual income tax return for financial year 2024-25', recurrence: 'annually', daysFromNow: 180 },

    // FBT deadlines
    { title: 'FY2025 FBT Return', deadlineType: 'fbt', description: 'Fringe Benefits Tax return for FY2025', recurrence: 'annually', daysFromNow: 75 },

    // PAYG deadlines
    { title: 'Q2 PAYG Instalment', deadlineType: 'payg', description: 'Pay As You Go instalment for Q2', recurrence: 'quarterly', daysFromNow: 21 },

    // SMSF deadlines
    { title: 'FY2024 SMSF Annual Return', deadlineType: 'smsf', description: 'Self-Managed Super Fund annual return and audit', recurrence: 'annually', daysFromNow: -10, completed: true, lodgementReference: 'SMSF-2024-SAR-11111' },
    { title: 'FY2025 SMSF Annual Return', deadlineType: 'smsf', description: 'Self-Managed Super Fund annual return and audit', recurrence: 'annually', daysFromNow: 200 },

    // Company returns
    { title: 'FY2025 Company Tax Return', deadlineType: 'company_return', description: 'Company income tax return', recurrence: 'annually', daysFromNow: 120 },

    // Trust returns
    { title: 'FY2025 Trust Tax Return', deadlineType: 'trust_return', description: 'Trust income tax return and distribution statement', recurrence: 'annually', daysFromNow: 150 },

    // Custom deadlines
    { title: 'Annual Review Meeting', deadlineType: 'custom', description: 'Yearly client review and planning meeting', recurrence: 'annually', daysFromNow: 30 },
    { title: 'GST Registration Review', deadlineType: 'custom', description: 'Review GST registration and turnover threshold', recurrence: 'none', daysFromNow: 5 },
  ];

  // Map client types to relevant deadline types
  const deadlinesByClientType: Record<ClientType, DeadlineType[]> = {
    individual: ['income_tax', 'bas', 'payg', 'custom'],
    company: ['company_return', 'bas', 'fbt', 'payg', 'custom'],
    trust: ['trust_return', 'bas', 'custom'],
    smsf: ['smsf', 'custom'],
    partnership: ['income_tax', 'bas', 'payg', 'custom'],
  };

  mockClients.forEach((client) => {
    const relevantTypes = deadlinesByClientType[client.clientType];

    // Filter templates to only relevant deadline types and add some variation
    const clientDeadlines = deadlineTemplates
      .filter(t => relevantTypes.includes(t.deadlineType))
      .slice(0, Math.floor(Math.random() * 3) + 3); // 3-5 deadlines per client

    clientDeadlines.forEach((template, index) => {
      const dueDate = new Date(now);
      // Add some variation to the days
      const variation = Math.floor(Math.random() * 10) - 5;
      dueDate.setDate(dueDate.getDate() + template.daysFromNow + variation);

      const completed = template.completed || false;
      const status = getStatus(dueDate, completed);

      const deadline: Deadline = {
        id: generateId(),
        tenantId,
        clientId: client.id,
        clientName: client.displayName,
        title: template.title,
        description: template.description,
        deadlineType: template.deadlineType,
        status,
        dueDate: dueDate.toISOString().split('T')[0],
        completedAt: completed ? new Date(dueDate.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString() : undefined,
        lodgementReference: template.lodgementReference,
        recurrence: template.recurrence,
        reminderDays: [7, 3, 1],
        notes: index === 0 ? 'Priority client - ensure early lodgement' : undefined,
        createdAt: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: now.toISOString(),
      };

      mockDeadlines.push(deadline);
    });
  });
}

// Initialize on module load
initializeMockData();

// Helper function to simulate network delay
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Mock API service matching the real API interface
export const mockClientsService = {
  // List clients with filters
  async listClients(filters?: ClientListFilters): Promise<ClientListResponse> {
    await delay(300);

    let filtered = [...mockClients];

    // Apply filters
    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(c =>
        c.displayName.toLowerCase().includes(search) ||
        c.clientCode.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search) ||
        c.abn?.includes(search)
      );
    }

    if (filters?.clientType) {
      const types = Array.isArray(filters.clientType) ? filters.clientType : [filters.clientType];
      filtered = filtered.filter(c => types.includes(c.clientType));
    }

    if (filters?.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
      filtered = filtered.filter(c => statuses.includes(c.status));
    }

    if (filters?.industry) {
      filtered = filtered.filter(c => c.industry?.toLowerCase().includes(filters.industry!.toLowerCase()));
    }

    // Sort
    const sortBy = filters?.sortBy || 'displayName';
    const sortOrder = filters?.sortOrder || 'asc';
    filtered.sort((a, b) => {
      const aVal = (a as any)[sortBy] || '';
      const bVal = (b as any)[sortBy] || '';
      const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    // Paginate
    const page = filters?.page || 1;
    const limit = filters?.limit || 20;
    const start = (page - 1) * limit;
    const paginated = filtered.slice(start, start + limit);

    // Add health scores to clients
    const clientsWithHealth = paginated.map(client => ({
      ...client,
      healthScore: this.generateHealthScore(client),
    }));

    return {
      clients: clientsWithHealth,
      total: filtered.length,
      page,
      limit,
      totalPages: Math.ceil(filtered.length / limit),
    };
  },

  // Get single client
  async getClient(id: string): Promise<Client> {
    await delay(200);
    const client = mockClients.find(c => c.id === id);
    if (!client) throw new Error('Client not found');
    return client;
  },

  // Create client
  async createClient(data: CreateClientRequest): Promise<Client> {
    await delay(300);
    const now = new Date().toISOString();
    const clientId = generateId();

    const client: Client = {
      id: clientId,
      tenantId: 'tenant-001',
      clientCode: data.clientCode,
      displayName: data.displayName,
      clientType: data.clientType || 'company',
      email: data.email,
      phone: data.phone,
      address: data.address,
      abn: data.abn,
      acn: data.acn,
      tfn: data.tfn,
      industry: data.industry,
      totalDocuments: 0,
      totalSizeBytes: 0,
      indexedDocuments: 0,
      tags: data.tags,
      status: data.status || 'active',
      createdAt: now,
      updatedAt: now,
    };

    mockClients.unshift(client);

    // Create default folders
    standardFolders.forEach((folderData) => {
      const folderId = generateId();
      mockFolders.push({
        id: folderId,
        tenantId: 'tenant-001',
        clientId,
        name: folderData.name,
        fullPath: `/${data.clientCode}/${folderData.name}`,
        s3Prefix: `clients/${clientId}/${folderId}/`,
        folderType: folderData.folderType,
        documentCount: 0,
        subfolderCount: 0,
        totalSizeBytes: 0,
        createdAt: now,
        updatedAt: now,
      });
    });

    return client;
  },

  // Update client
  async updateClient(id: string, data: UpdateClientRequest): Promise<Client> {
    await delay(300);
    const index = mockClients.findIndex(c => c.id === id);
    if (index === -1) throw new Error('Client not found');

    mockClients[index] = {
      ...mockClients[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    return mockClients[index];
  },

  // Delete client
  async deleteClient(id: string): Promise<void> {
    await delay(300);
    const index = mockClients.findIndex(c => c.id === id);
    if (index === -1) throw new Error('Client not found');
    mockClients.splice(index, 1);

    // Delete related folders
    const indicesToRemove = mockFolders
      .map((f, i) => f.clientId === id ? i : -1)
      .filter(i => i !== -1)
      .reverse();
    indicesToRemove.forEach(i => mockFolders.splice(i, 1));
  },

  // Get client stats
  async getClientStats(): Promise<ClientStats> {
    await delay(200);
    const active = mockClients.filter(c => c.status === 'active').length;
    const inactive = mockClients.filter(c => c.status === 'inactive').length;
    const archived = mockClients.filter(c => c.status === 'archived').length;

    const byType: Record<ClientType, number> = {
      individual: 0,
      company: 0,
      trust: 0,
      smsf: 0,
      partnership: 0,
    };
    mockClients.forEach(c => {
      byType[c.clientType]++;
    });

    return {
      totalClients: mockClients.length,
      activeClients: active,
      inactiveClients: inactive,
      archivedClients: archived,
      clientsByType: byType,
      totalDocuments: mockClients.reduce((sum, c) => sum + c.totalDocuments, 0),
      totalSizeBytes: mockClients.reduce((sum, c) => sum + c.totalSizeBytes, 0),
      indexedDocuments: mockClients.reduce((sum, c) => sum + c.indexedDocuments, 0),
    };
  },

  // Get client folders (root level)
  async getClientFolders(clientId: string): Promise<Folder[]> {
    await delay(200);
    return mockFolders.filter(f => f.clientId === clientId && !f.parentFolderId);
  },

  // Get folder by ID
  async getFolder(id: string): Promise<Folder> {
    await delay(200);
    const folder = mockFolders.find(f => f.id === id);
    if (!folder) throw new Error('Folder not found');
    return folder;
  },

  // Get folder contents
  async getFolderContents(folderId: string): Promise<FolderContents> {
    await delay(300);
    const folder = mockFolders.find(f => f.id === folderId);
    if (!folder) throw new Error('Folder not found');

    const subfolders = mockFolders.filter(f => f.parentFolderId === folderId);

    // Generate mock documents
    const documents: ClientDocument[] = [];
    for (let i = 0; i < Math.min(folder.documentCount, 10); i++) {
      const doc = sampleDocuments[i % sampleDocuments.length];
      documents.push({
        id: generateId(),
        tenantId: folder.tenantId,
        clientId: folder.clientId,
        folderId,
        userCognitoSub: 'user-001',
        filename: doc.name,
        originalFilename: doc.name,
        fileType: doc.name.split('.').pop() || '',
        fileSizeBytes: doc.size + Math.floor(Math.random() * 50000),
        mimeType: doc.type,
        s3Bucket: 'aipa-documents',
        s3Key: `${folder.s3Prefix}${doc.name}`,
        financialYear: 'FY2024',
        bedrockStatus: Math.random() > 0.2 ? 'indexed' : 'pending',
        createdAt: folder.createdAt,
        updatedAt: folder.updatedAt,
      });
    }

    // Build breadcrumbs
    const breadcrumbs: FolderBreadcrumb[] = [];
    let currentId: string | undefined = folderId;
    while (currentId) {
      const f = mockFolders.find(mf => mf.id === currentId);
      if (!f) break;
      breadcrumbs.unshift({
        id: f.id,
        name: f.name,
        fullPath: f.fullPath,
      });
      currentId = f.parentFolderId;
    }

    return {
      folder,
      subfolders,
      documents,
      breadcrumbs,
    };
  },

  // Create folder
  async createFolder(data: CreateFolderRequest): Promise<Folder> {
    await delay(300);
    const now = new Date().toISOString();
    const folderId = generateId();

    // Get parent folder path if exists
    let fullPath = '';
    if (data.parentFolderId) {
      const parent = mockFolders.find(f => f.id === data.parentFolderId);
      if (parent) {
        fullPath = `${parent.fullPath}/${data.name}`;
        parent.subfolderCount++;
      }
    } else if (data.clientId) {
      const client = mockClients.find(c => c.id === data.clientId);
      if (client) {
        fullPath = `/${client.clientCode}/${data.name}`;
      }
    }

    const folder: Folder = {
      id: folderId,
      tenantId: 'tenant-001',
      clientId: data.clientId,
      parentFolderId: data.parentFolderId,
      name: data.name,
      fullPath,
      s3Prefix: `clients/${data.clientId || 'shared'}/${folderId}/`,
      folderType: data.folderType || 'general',
      documentCount: 0,
      subfolderCount: 0,
      totalSizeBytes: 0,
      createdAt: now,
      updatedAt: now,
    };

    mockFolders.push(folder);
    return folder;
  },

  // Update folder
  async updateFolder(id: string, data: UpdateFolderRequest): Promise<Folder> {
    await delay(300);
    const index = mockFolders.findIndex(f => f.id === id);
    if (index === -1) throw new Error('Folder not found');

    mockFolders[index] = {
      ...mockFolders[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    return mockFolders[index];
  },

  // Delete folder
  async deleteFolder(id: string): Promise<void> {
    await delay(300);
    const index = mockFolders.findIndex(f => f.id === id);
    if (index === -1) throw new Error('Folder not found');

    // Check for subfolders
    const hasSubfolders = mockFolders.some(f => f.parentFolderId === id);
    if (hasSubfolders) {
      throw new Error('Cannot delete folder with subfolders');
    }

    // Update parent subfolder count
    const folder = mockFolders[index];
    if (folder.parentFolderId) {
      const parent = mockFolders.find(f => f.id === folder.parentFolderId);
      if (parent) parent.subfolderCount--;
    }

    mockFolders.splice(index, 1);
  },

  // Get folder stats
  async getFolderStats(): Promise<FolderStats> {
    await delay(200);
    const byType: Record<FolderType, number> = {
      client_root: 0,
      tax_returns: 0,
      bas: 0,
      financials: 0,
      correspondence: 0,
      workpapers: 0,
      general: 0,
    };
    mockFolders.forEach(f => {
      byType[f.folderType]++;
    });

    return {
      totalFolders: mockFolders.length,
      foldersByType: byType,
      totalDocuments: mockFolders.reduce((sum, f) => sum + f.documentCount, 0),
      totalSizeBytes: mockFolders.reduce((sum, f) => sum + f.totalSizeBytes, 0),
    };
  },

  // ========== DEADLINE METHODS ==========

  // List deadlines with filters
  async listDeadlines(filters?: DeadlineListFilters): Promise<DeadlineListResponse> {
    await delay(300);

    let filtered = [...mockDeadlines];

    // Apply filters
    if (filters?.clientId) {
      filtered = filtered.filter(d => d.clientId === filters.clientId);
    }

    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(d =>
        d.title.toLowerCase().includes(search) ||
        d.description?.toLowerCase().includes(search) ||
        d.clientName?.toLowerCase().includes(search)
      );
    }

    if (filters?.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
      filtered = filtered.filter(d => statuses.includes(d.status));
    }

    if (filters?.deadlineType) {
      const types = Array.isArray(filters.deadlineType) ? filters.deadlineType : [filters.deadlineType];
      filtered = filtered.filter(d => types.includes(d.deadlineType));
    }

    if (filters?.dueDateFrom) {
      filtered = filtered.filter(d => d.dueDate >= filters.dueDateFrom!);
    }

    if (filters?.dueDateTo) {
      filtered = filtered.filter(d => d.dueDate <= filters.dueDateTo!);
    }

    // Sort
    const sortBy = filters?.sortBy || 'dueDate';
    const sortOrder = filters?.sortOrder || 'asc';
    filtered.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (sortBy === 'status') {
        const statusOrder: Record<DeadlineStatus, number> = { overdue: 0, due_soon: 1, upcoming: 2, completed: 3 };
        aVal = statusOrder[a.status];
        bVal = statusOrder[b.status];
      } else {
        aVal = (a as any)[sortBy] || '';
        bVal = (b as any)[sortBy] || '';
      }

      const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    // Paginate
    const page = filters?.page || 1;
    const limit = filters?.limit || 20;
    const start = (page - 1) * limit;
    const paginated = filtered.slice(start, start + limit);

    return {
      deadlines: paginated,
      total: filtered.length,
      page,
      limit,
      totalPages: Math.ceil(filtered.length / limit),
    };
  },

  // Get single deadline
  async getDeadline(id: string): Promise<Deadline> {
    await delay(200);
    const deadline = mockDeadlines.find(d => d.id === id);
    if (!deadline) throw new Error('Deadline not found');
    return deadline;
  },

  // Create deadline
  async createDeadline(data: CreateDeadlineRequest): Promise<Deadline> {
    await delay(300);
    const now = new Date().toISOString();
    const deadlineId = generateId();

    // Get client name
    const client = mockClients.find(c => c.id === data.clientId);

    // Calculate initial status
    const dueDate = new Date(data.dueDate);
    const today = new Date();
    const daysUntil = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    let status: DeadlineStatus = 'upcoming';
    if (daysUntil < 0) status = 'overdue';
    else if (daysUntil <= 7) status = 'due_soon';

    const deadline: Deadline = {
      id: deadlineId,
      tenantId: 'tenant-001',
      clientId: data.clientId,
      clientName: client?.displayName,
      title: data.title,
      description: data.description,
      deadlineType: data.deadlineType,
      status,
      dueDate: data.dueDate,
      recurrence: data.recurrence || 'none',
      reminderDays: data.reminderDays || [7, 3, 1],
      notes: data.notes,
      createdAt: now,
      updatedAt: now,
    };

    mockDeadlines.unshift(deadline);
    return deadline;
  },

  // Update deadline
  async updateDeadline(id: string, data: UpdateDeadlineRequest): Promise<Deadline> {
    await delay(300);
    const index = mockDeadlines.findIndex(d => d.id === id);
    if (index === -1) throw new Error('Deadline not found');

    // Recalculate status if due date changes and status is not being explicitly set
    if (data.dueDate && !data.status && mockDeadlines[index].status !== 'completed') {
      const dueDate = new Date(data.dueDate);
      const today = new Date();
      const daysUntil = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (daysUntil < 0) data.status = 'overdue';
      else if (daysUntil <= 7) data.status = 'due_soon';
      else data.status = 'upcoming';
    }

    mockDeadlines[index] = {
      ...mockDeadlines[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    return mockDeadlines[index];
  },

  // Mark deadline as completed
  async completeDeadline(id: string, lodgementReference?: string): Promise<Deadline> {
    await delay(300);
    const index = mockDeadlines.findIndex(d => d.id === id);
    if (index === -1) throw new Error('Deadline not found');

    mockDeadlines[index] = {
      ...mockDeadlines[index],
      status: 'completed',
      completedAt: new Date().toISOString(),
      lodgementReference,
      updatedAt: new Date().toISOString(),
    };
    return mockDeadlines[index];
  },

  // Delete deadline
  async deleteDeadline(id: string): Promise<void> {
    await delay(300);
    const index = mockDeadlines.findIndex(d => d.id === id);
    if (index === -1) throw new Error('Deadline not found');
    mockDeadlines.splice(index, 1);
  },

  // Get deadline stats
  async getDeadlineStats(): Promise<DeadlineStats> {
    await delay(200);

    const byType: Record<DeadlineType, number> = {
      bas: 0,
      income_tax: 0,
      fbt: 0,
      payg: 0,
      smsf: 0,
      company_return: 0,
      trust_return: 0,
      custom: 0,
    };

    mockDeadlines.forEach(d => {
      byType[d.deadlineType]++;
    });

    return {
      totalDeadlines: mockDeadlines.length,
      upcoming: mockDeadlines.filter(d => d.status === 'upcoming').length,
      dueSoon: mockDeadlines.filter(d => d.status === 'due_soon').length,
      overdue: mockDeadlines.filter(d => d.status === 'overdue').length,
      completed: mockDeadlines.filter(d => d.status === 'completed').length,
      byType,
    };
  },

  // Get deadlines for a specific client
  async getClientDeadlines(clientId: string): Promise<Deadline[]> {
    await delay(200);
    return mockDeadlines
      .filter(d => d.clientId === clientId)
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  },

  // ========== GLOBAL SEARCH METHODS ==========

  // Global search across clients, folders, documents, and deadlines
  async globalSearch(query: string, limit: number = 20): Promise<GlobalSearchResponse> {
    await delay(300);

    if (!query || query.length < 2) {
      return { results: [], total: 0, query };
    }

    const searchLower = query.toLowerCase();
    const results: GlobalSearchResult[] = [];

    // Search clients
    mockClients.forEach(client => {
      let score = 0;
      let matchedField = '';

      if (client.displayName.toLowerCase().includes(searchLower)) {
        score += 100;
        matchedField = 'name';
      }
      if (client.clientCode.toLowerCase().includes(searchLower)) {
        score += 90;
        matchedField = matchedField || 'code';
      }
      if (client.email?.toLowerCase().includes(searchLower)) {
        score += 80;
        matchedField = matchedField || 'email';
      }
      if (client.abn?.includes(searchLower)) {
        score += 70;
        matchedField = matchedField || 'ABN';
      }
      if (client.industry?.toLowerCase().includes(searchLower)) {
        score += 50;
        matchedField = matchedField || 'industry';
      }

      if (score > 0) {
        results.push({
          type: 'client',
          id: client.id,
          title: client.displayName,
          subtitle: `${client.clientCode} - ${client.clientType}`,
          description: client.serviceDescription?.slice(0, 100),
          matchedField,
          score,
        });
      }
    });

    // Search deadlines
    mockDeadlines.forEach(deadline => {
      let score = 0;
      let matchedField = '';

      if (deadline.title.toLowerCase().includes(searchLower)) {
        score += 90;
        matchedField = 'title';
      }
      if (deadline.description?.toLowerCase().includes(searchLower)) {
        score += 70;
        matchedField = matchedField || 'description';
      }
      if (deadline.clientName?.toLowerCase().includes(searchLower)) {
        score += 60;
        matchedField = matchedField || 'client';
      }
      if (deadline.lodgementReference?.toLowerCase().includes(searchLower)) {
        score += 80;
        matchedField = matchedField || 'reference';
      }

      if (score > 0) {
        results.push({
          type: 'deadline',
          id: deadline.id,
          title: deadline.title,
          subtitle: `Due: ${deadline.dueDate} - ${deadline.status}`,
          description: deadline.description,
          clientId: deadline.clientId,
          clientName: deadline.clientName,
          matchedField,
          score,
        });
      }
    });

    // Search folders
    mockFolders.forEach(folder => {
      let score = 0;
      let matchedField = '';

      if (folder.name.toLowerCase().includes(searchLower)) {
        score += 70;
        matchedField = 'name';
      }
      if (folder.fullPath.toLowerCase().includes(searchLower)) {
        score += 50;
        matchedField = matchedField || 'path';
      }

      if (score > 0) {
        const client = mockClients.find(c => c.id === folder.clientId);
        results.push({
          type: 'folder',
          id: folder.id,
          title: folder.name,
          subtitle: folder.fullPath,
          clientId: folder.clientId,
          clientName: client?.displayName,
          matchedField,
          score,
        });
      }
    });

    // Sort by score descending
    results.sort((a, b) => (b.score || 0) - (a.score || 0));

    // Limit results
    const limited = results.slice(0, limit);

    return {
      results: limited,
      total: results.length,
      query,
    };
  },

  // ========== DOCUMENT METHODS ==========

  // List all documents with filters
  async listAllDocuments(filters?: any): Promise<any> {
    await delay(300);

    // Mock documents - in real app this would come from database
    const mockDocuments: any[] = mockClients.slice(0, 10).map((client, idx) => ({
      id: generateId(),
      tenantId: 'tenant-001',
      clientId: client.id,
      folderId: null,
      userCognitoSub: 'user-123',
      filename: `document_${idx + 1}.pdf`,
      originalFilename: `Client ${client.displayName} - Tax Return 2024.pdf`,
      fileType: 'pdf',
      fileSizeBytes: Math.floor(Math.random() * 5000000) + 100000,
      mimeType: 'application/pdf',
      s3Bucket: 'roni-docs',
      s3Key: `clients/${client.id}/document_${idx + 1}.pdf`,
      documentCategory: ['tax_return', 'bas', 'financial_statement', 'correspondence'][idx % 4],
      financialYear: '2024',
      aiSummary: 'This document contains financial information for the client',
      bedrockStatus: idx % 3 === 0 ? 'indexed' : 'pending',
      description: `Financial document for ${client.displayName}`,
      tags: ['FY2024', 'Tax Return'],
      createdAt: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date().toISOString(),
    }));

    let filtered = mockDocuments;

    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(d =>
        d.originalFilename.toLowerCase().includes(search) ||
        d.description?.toLowerCase().includes(search)
      );
    }

    if (filters?.category && filters.category !== 'all') {
      filtered = filtered.filter(d => d.documentCategory === filters.category);
    }

    return {
      documents: filtered,
      total: filtered.length,
      page: 1,
      limit: filters?.limit || 100,
      totalPages: 1,
    };
  },

  // List document templates
  async listDocumentTemplates(filters?: any): Promise<any[]> {
    await delay(200);

    const templates = [
      {
        id: generateId(),
        tenantId: 'tenant-001',
        name: 'Engagement Letter - Individual',
        description: 'Standard engagement letter for individual tax returns',
        category: 'correspondence',
        filename: 'engagement_letter_individual.docx',
        fileType: 'docx',
        fileSizeBytes: 45000,
        s3Key: 'templates/engagement_letter_individual.docx',
        isActive: true,
        usageCount: 156,
        createdBy: 'admin',
        createdAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        tenantId: 'tenant-001',
        name: 'Tax Return Checklist',
        description: 'Checklist for preparing individual tax returns',
        category: 'tax_return',
        filename: 'tax_return_checklist.pdf',
        fileType: 'pdf',
        fileSizeBytes: 128000,
        s3Key: 'templates/tax_return_checklist.pdf',
        isActive: true,
        usageCount: 342,
        createdBy: 'admin',
        createdAt: new Date(Date.now() - 200 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        tenantId: 'tenant-001',
        name: 'BAS Lodgement Form',
        description: 'Business Activity Statement lodgement template',
        category: 'bas',
        filename: 'bas_lodgement.xlsx',
        fileType: 'xlsx',
        fileSizeBytes: 75000,
        s3Key: 'templates/bas_lodgement.xlsx',
        isActive: true,
        usageCount: 289,
        createdBy: 'admin',
        createdAt: new Date(Date.now() - 300 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        tenantId: 'tenant-001',
        name: 'Financial Statement Template',
        description: 'Standard financial statement format',
        category: 'financial_statement',
        filename: 'financial_statement.xlsx',
        fileType: 'xlsx',
        fileSizeBytes: 92000,
        s3Key: 'templates/financial_statement.xlsx',
        isActive: true,
        usageCount: 178,
        createdBy: 'admin',
        createdAt: new Date(Date.now() - 250 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ];

    let filtered = templates;

    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(search) ||
        t.description?.toLowerCase().includes(search)
      );
    }

    if (filters?.category && filters.category !== 'all') {
      filtered = filtered.filter(t => t.category === filters.category);
    }

    return filtered;
  },

  // Create document template
  async createDocumentTemplate(data: any): Promise<any> {
    await delay(500);

    const template = {
      id: generateId(),
      tenantId: 'tenant-001',
      name: data.name,
      description: data.description,
      category: data.category,
      filename: data.file.name,
      fileType: data.file.name.split('.').pop() || 'unknown',
      fileSizeBytes: data.file.size,
      s3Key: `templates/${generateId()}_${data.file.name}`,
      isActive: true,
      usageCount: 0,
      createdBy: 'current-user',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return template;
  },

  // Delete document template
  async deleteDocumentTemplate(id: string): Promise<void> {
    await delay(300);
    console.log('Deleting template:', id);
  },

  // Get document versions
  async getDocumentVersions(documentId: string): Promise<any[]> {
    await delay(200);

    const versions = [
      {
        id: generateId(),
        documentId,
        versionNumber: 3,
        filename: 'document_v3.pdf',
        fileSizeBytes: 2500000,
        s3Key: `versions/${documentId}/v3.pdf`,
        uploadedBy: 'user-123',
        uploadedByName: 'John Smith',
        changeDescription: 'Updated financial figures for Q4',
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: generateId(),
        documentId,
        versionNumber: 2,
        filename: 'document_v2.pdf',
        fileSizeBytes: 2400000,
        s3Key: `versions/${documentId}/v2.pdf`,
        uploadedBy: 'user-124',
        uploadedByName: 'Jane Doe',
        changeDescription: 'Added appendix with supporting documents',
        createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: generateId(),
        documentId,
        versionNumber: 1,
        filename: 'document_v1.pdf',
        fileSizeBytes: 2300000,
        s3Key: `versions/${documentId}/v1.pdf`,
        uploadedBy: 'user-123',
        uploadedByName: 'John Smith',
        changeDescription: 'Initial version',
        createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ];

    return versions;
  },

  // Get action items
  async getActionItems(filters?: { priority?: string; category?: string; status?: string }): Promise<any[]> {
    await delay(300);

    const actionItems = [
      {
        id: generateId(),
        clientId: mockClients[0].id,
        clientName: mockClients[0].displayName,
        clientType: mockClients[0].clientType,
        category: 'id_renewal',
        priority: 'urgent',
        title: 'Director ID Expired',
        description: 'Director identification document has expired. Required for compliance.',
        dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        estimatedTime: 15,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[1].id,
        clientName: mockClients[1].displayName,
        clientType: mockClients[1].clientType,
        category: 'document_expiry',
        priority: 'urgent',
        title: 'Trust Deed Review Overdue',
        description: 'Trust deed requires 5-year review. Last review was 6 years ago.',
        dueDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        estimatedTime: 60,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[2].id,
        clientName: mockClients[2].displayName,
        clientType: mockClients[2].clientType,
        category: 'compliance',
        priority: 'high',
        title: 'SMSF Investment Strategy Outdated',
        description: 'Investment strategy not updated for 3 years. ATO requires annual review.',
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        estimatedTime: 45,
        status: 'pending',
        assignedTo: 'Sarah Chen',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[3].id,
        clientName: mockClients[3].displayName,
        clientType: mockClients[3].clientType,
        category: 'lodgement',
        priority: 'high',
        title: 'BAS Lodgement Due',
        description: 'Q2 BAS lodgement due in 14 days. Working papers not yet prepared.',
        dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        estimatedTime: 90,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[4].id,
        clientName: mockClients[4].displayName,
        clientType: mockClients[4].clientType,
        category: 'review',
        priority: 'medium',
        title: 'GST Inconsistency Detected',
        description: 'BAS lodged but GST working papers missing. Requires review.',
        dueDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
        estimatedTime: 30,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[5].id,
        clientName: mockClients[5].displayName,
        clientType: mockClients[5].clientType,
        category: 'id_renewal',
        priority: 'high',
        title: 'Passport Expiring Soon',
        description: 'Client passport expires in 45 days. Update required for verification.',
        dueDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString(),
        estimatedTime: 10,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[6].id,
        clientName: mockClients[6].displayName,
        clientType: mockClients[6].clientType,
        category: 'follow_up',
        priority: 'medium',
        title: 'Rental Income Documentation Missing',
        description: 'Rental income declared but no lease agreement on file.',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        estimatedTime: 20,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[7].id,
        clientName: mockClients[7].displayName,
        clientType: mockClients[7].clientType,
        category: 'compliance',
        priority: 'high',
        title: 'ASIC Annual Review Due',
        description: 'Annual ASIC review statement required within 30 days.',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        estimatedTime: 25,
        status: 'pending',
        assignedTo: 'Mike Johnson',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[8].id,
        clientName: mockClients[8].displayName,
        clientType: mockClients[8].clientType,
        category: 'review',
        priority: 'medium',
        title: 'Prior Year Data Inconsistency',
        description: 'Current year claims not aligned with prior year data.',
        dueDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
        estimatedTime: 45,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[9].id,
        clientName: mockClients[9].displayName,
        clientType: mockClients[9].clientType,
        category: 'id_renewal',
        priority: 'low',
        title: 'Driver License Expiring',
        description: 'Driver license expires in 90 days. Consider updating ID documents.',
        dueDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
        estimatedTime: 10,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ];

    return actionItems.filter(item => {
      if (filters?.priority && filters.priority !== 'all' && item.priority !== filters.priority) return false;
      if (filters?.category && filters.category !== 'all' && item.category !== filters.category) return false;
      if (filters?.status && item.status !== filters.status) return false;
      return true;
    });
  },

  // Update action status
  async updateActionStatus(id: string, status: string): Promise<void> {
    await delay(200);
    console.log('Updating action:', id, 'to status:', status);
  },

  // Get revenue opportunities
  async getRevenueOpportunities(filters?: { type?: string; status?: string }): Promise<any[]> {
    await delay(300);

    const opportunities = [
      {
        id: generateId(),
        clientId: mockClients[2].id,
        clientName: mockClients[2].displayName,
        clientType: mockClients[2].clientType,
        opportunityType: 'investment',
        title: 'SMSF Excess Cash Investment Advisory',
        description: 'SMSF holding $285,000 in cash (42% of total assets). Significantly above recommended 10-15% allocation.',
        reasoning: 'Analysis of SMSF balance sheet shows excessive cash holdings. Current inflation rate of 5.2% is eroding real value. Client would benefit from diversified investment strategy aligned with retirement goals.',
        estimatedValue: 8500,
        confidence: 92,
        status: 'identified',
        identifiedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        identifiedBy: 'ai',
        nextSteps: [
          'Review current investment strategy document',
          'Analyze client risk profile and retirement timeline',
          'Prepare investment proposal with asset allocation options',
          'Schedule client meeting to discuss recommendations',
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[0].id,
        clientName: mockClients[0].displayName,
        clientType: mockClients[0].clientType,
        opportunityType: 'restructure',
        title: 'Trust Structure Optimization',
        description: 'Trust income distribution showing potential tax inefficiency. Income concentrated in high-tax bracket beneficiaries.',
        reasoning: 'Current trust distribution pattern results in marginal tax rate of 45% for primary beneficiary. Restructuring distributions could save approximately $15,000 annually in tax.',
        estimatedValue: 12000,
        confidence: 85,
        status: 'contacted',
        identifiedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        identifiedBy: 'ai',
        contactedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        nextSteps: [
          'Model alternative distribution scenarios',
          'Assess family group tax positions',
          'Prepare tax comparison report',
          'Draft updated trust resolution',
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[3].id,
        clientName: mockClients[3].displayName,
        clientType: mockClients[3].clientType,
        opportunityType: 'planning',
        title: 'Division 7A Exposure - Preventative Planning',
        description: 'Company has made significant loans to shareholders without proper documentation. Potential Division 7A deemed dividend risk.',
        reasoning: 'Identified $180,000 in shareholder loans without compliant loan agreements. This creates deemed dividend exposure at marginal tax rates. Urgent remediation required before year-end.',
        estimatedValue: 6500,
        confidence: 95,
        status: 'proposal_sent',
        identifiedAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
        identifiedBy: 'ai',
        contactedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        nextSteps: [
          'Draft compliant Division 7A loan agreements',
          'Calculate benchmark interest rates',
          'Prepare board resolutions',
          'Document repayment schedules',
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[1].id,
        clientName: mockClients[1].displayName,
        clientType: mockClients[1].clientType,
        opportunityType: 'advisory',
        title: 'Trust Vesting Date Approaching',
        description: 'Discretionary trust vesting date is in 18 months. No succession plan documented.',
        reasoning: 'Trust deed shows vesting date of June 2026. Without action, trust assets will vest absolutely, losing tax planning flexibility. Early planning essential for optimal restructure.',
        estimatedValue: 15000,
        confidence: 88,
        status: 'identified',
        identifiedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        identifiedBy: 'ai',
        nextSteps: [
          'Review trust deed vesting provisions',
          'Assess asset values and CGT positions',
          'Model vesting vs restructure scenarios',
          'Engage legal advisor for deed amendment options',
          'Prepare succession planning recommendations',
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[4].id,
        clientName: mockClients[4].displayName,
        clientType: mockClients[4].clientType,
        opportunityType: 'compliance_upgrade',
        title: 'Corporate Tax Governance Review',
        description: 'Company tax returns show minimal director fees despite significant trading profit. Potential compliance and planning opportunity.',
        reasoning: 'Company paying 30% tax on profits but directors receiving minimal salaries. Better remuneration structure could optimize tax and superannuation outcomes while improving governance.',
        estimatedValue: 4500,
        confidence: 78,
        status: 'identified',
        identifiedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        identifiedBy: 'ai',
        nextSteps: [
          'Analyze optimal salary vs dividend mix',
          'Review superannuation contribution capacity',
          'Model tax outcomes for family unit',
          'Prepare remuneration strategy paper',
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[5].id,
        clientName: mockClients[5].displayName,
        clientType: mockClients[5].clientType,
        opportunityType: 'additional_service',
        title: 'Property Portfolio - Depreciation Schedule',
        description: 'Client owns 3 investment properties. No depreciation schedules on file.',
        reasoning: 'Investment properties acquired 2019-2022. Professional depreciation schedules typically identify $8,000-$15,000 in first-year deductions per property. Client missing significant tax deductions.',
        estimatedValue: 3500,
        confidence: 90,
        status: 'won',
        identifiedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
        identifiedBy: 'ai',
        contactedAt: new Date(Date.now() - 38 * 24 * 60 * 60 * 1000).toISOString(),
        wonAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        actualValue: 3500,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[6].id,
        clientName: mockClients[6].displayName,
        clientType: mockClients[6].clientType,
        opportunityType: 'planning',
        title: 'Capital Gains Tax Planning - Property Sale',
        description: 'Investment property held since 2015. Client mentioned considering sale in tax return notes.',
        reasoning: 'Long-term capital gain likely. Multiple planning strategies available: timing of sale, use of main residence exemption, small business CGT concessions if eligibility exists.',
        estimatedValue: 7500,
        confidence: 82,
        status: 'contacted',
        identifiedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        identifiedBy: 'ai',
        contactedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        nextSteps: [
          'Calculate estimated capital gain',
          'Model different sale timing scenarios',
          'Assess eligibility for CGT concessions',
          'Review asset ownership structures',
          'Prepare CGT planning report',
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: generateId(),
        clientId: mockClients[7].id,
        clientName: mockClients[7].displayName,
        clientType: mockClients[7].clientType,
        opportunityType: 'advisory',
        title: 'Succession Planning - Family Business',
        description: 'Family company with second-generation family members entering business. No formal succession plan.',
        reasoning: 'Directors are aged 62 and 65. Next generation actively involved in operations but no share transfer or succession documented. Early planning critical for tax-effective transition.',
        estimatedValue: 18000,
        confidence: 85,
        status: 'identified',
        identifiedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        identifiedBy: 'ai',
        nextSteps: [
          'Document current ownership structure',
          'Interview family members on intentions',
          'Value business for stamp duty purposes',
          'Model succession transition options',
          'Engage legal for shareholders agreement',
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ];

    return opportunities.filter(opp => {
      if (filters?.type && filters.type !== 'all' && opp.opportunityType !== filters.type) return false;
      if (filters?.status && filters.status !== 'all' && opp.status !== filters.status) return false;
      return true;
    });
  },

  // Update opportunity status
  async updateOpportunityStatus(id: string, status: string): Promise<void> {
    await delay(200);
    console.log('Updating opportunity:', id, 'to status:', status);
  },

  // Generate client health score
  generateHealthScore(client: any): any {
    // Mock health score generation
    const score = Math.floor(Math.random() * 50) + 50; // 50-100
    const level = score >= 80 ? 'excellent' : score >= 65 ? 'good' : score >= 50 ? 'needs_attention' : 'critical';

    const issues = [];
    const missingDocs = [];
    const expiredDocs = [];
    const risks = [];

    // Add some mock issues based on score
    if (score < 70) {
      issues.push({
        id: generateId(),
        severity: 'warning',
        category: 'documentation',
        title: 'Missing Trust Resolution',
        description: 'Annual trust resolution for distribution not found in file',
        impact: 'Required for tax compliance and audit defense',
        recommendation: 'Request and file current year trust resolution',
        createdAt: new Date().toISOString(),
      });
      missingDocs.push('Trust Distribution Resolution FY2024');
    }

    if (score < 65) {
      expiredDocs.push({
        documentName: 'Director ID - John Smith',
        expiryDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        daysOverdue: 30,
        documentType: 'identification',
      });
      issues.push({
        id: generateId(),
        severity: 'critical',
        category: 'expiry',
        title: 'Expired Director Identification',
        description: 'Director ID expired 30 days ago',
        impact: 'Non-compliance with ATO requirements',
        recommendation: 'Update director identification immediately',
        createdAt: new Date().toISOString(),
      });
    }

    if (score < 75) {
      risks.push({
        riskType: 'gst_inconsistency',
        severity: 'medium',
        title: 'GST Working Papers Missing',
        description: 'BAS lodged for Q2 but supporting working papers not in file',
        affectedPeriod: 'Q2 FY2024',
        estimatedImpact: 'Audit defense weakness',
      });
    }

    return {
      score,
      level,
      lastCalculated: new Date().toISOString(),
      issues,
      missingDocuments: missingDocs,
      expiredDocuments: expiredDocs,
      complianceRisks: risks,
      recommendations: [
        score < 70 ? 'Complete missing documentation within 30 days' : 'Maintain current compliance standards',
        'Schedule quarterly file review',
        'Update client contact information',
      ],
    };
  },

  // Get risk heatmap data
  async getRiskHeatmap(filters?: { riskLevel?: string; category?: string }): Promise<any> {
    await delay(400);

    const allClients = await this.listClients({ limit: 100 });
    let clientsWithRisk = allClients.clients.map((client: any) => {
      const healthScore = this.generateHealthScore(client);
      const overallRisk = healthScore.score >= 80 ? 'none' :
                         healthScore.score >= 65 ? 'low' :
                         healthScore.score >= 50 ? 'medium' :
                         healthScore.score >= 35 ? 'high' : 'critical';

      return {
        clientId: client.id,
        clientName: client.displayName,
        clientType: client.clientType,
        overallRisk,
        healthScore: healthScore.score,
        riskBreakdown: {
          compliance: healthScore.complianceRisks.length > 0 ? 'high' : healthScore.score >= 70 ? 'low' : 'medium',
          documentation: healthScore.missingDocuments.length > 2 ? 'high' : healthScore.missingDocuments.length > 0 ? 'medium' : 'low',
          expiry: healthScore.expiredDocuments.length > 0 ? 'critical' : 'none',
          financial: Math.random() > 0.7 ? 'medium' : 'low',
          operational: healthScore.score >= 75 ? 'low' : 'medium',
        },
        criticalIssues: healthScore.issues.filter((i: any) => i.severity === 'critical').length,
        highIssues: healthScore.issues.filter((i: any) => i.severity === 'warning').length,
        mediumIssues: healthScore.issues.filter((i: any) => i.severity === 'info').length,
        lastAssessed: new Date().toISOString(),
      };
    });

    // Apply filters
    if (filters?.riskLevel && filters.riskLevel !== 'all') {
      clientsWithRisk = clientsWithRisk.filter((c: any) => c.overallRisk === filters.riskLevel);
    }

    if (filters?.category && filters.category !== 'all') {
      clientsWithRisk = clientsWithRisk.filter((c: any) =>
        c.riskBreakdown[filters.category!] === 'high' || c.riskBreakdown[filters.category!] === 'critical'
      );
    }

    const stats = {
      totalClients: allClients.total,
      criticalRisk: clientsWithRisk.filter((c: any) => c.overallRisk === 'critical').length,
      highRisk: clientsWithRisk.filter((c: any) => c.overallRisk === 'high').length,
      mediumRisk: clientsWithRisk.filter((c: any) => c.overallRisk === 'medium').length,
      lowRisk: clientsWithRisk.filter((c: any) => c.overallRisk === 'low').length,
      noRisk: clientsWithRisk.filter((c: any) => c.overallRisk === 'none').length,
      averageHealthScore: Math.round(clientsWithRisk.reduce((sum: number, c: any) => sum + c.healthScore, 0) / clientsWithRisk.length),
      totalIssues: clientsWithRisk.reduce((sum: number, c: any) => sum + c.criticalIssues + c.highIssues + c.mediumIssues, 0),
    };

    return {
      clients: clientsWithRisk,
      stats,
    };
  },

  // Get client timeline events
  async getClientTimeline(filters?: { clientId?: string; eventType?: string; search?: string }): Promise<any> {
    await delay(400);

    const allClients = await this.listClients({ limit: 100 });
    const clientsList = filters?.clientId ? allClients.clients.filter((c: any) => c.id === filters.clientId) : allClients.clients;

    const eventTypes = [
      'document_upload', 'deadline_created', 'deadline_completed', 'meeting', 'email',
      'phone_call', 'tax_lodgement', 'bas_lodgement', 'note', 'status_change',
      'health_score_change', 'issue_detected', 'issue_resolved'
    ];

    const events: any[] = [];

    clientsList.forEach((client: any) => {
      // Generate 5-15 events per client
      const numEvents = Math.floor(Math.random() * 10) + 5;

      for (let i = 0; i < numEvents; i++) {
        const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
        const daysAgo = Math.floor(Math.random() * 180); // Events from last 6 months
        const timestamp = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);

        const eventTitles: Record<string, string> = {
          document_upload: 'Uploaded 2023 Tax Return',
          deadline_created: 'BAS Q4 2023 Deadline Set',
          deadline_completed: 'PAYG Lodgement Completed',
          meeting: 'Annual Review Meeting',
          email: 'Sent Financial Year Summary',
          phone_call: 'Discussed Tax Planning',
          tax_lodgement: '2023 Income Tax Lodged',
          bas_lodgement: 'Q4 2023 BAS Lodged',
          note: 'Added compliance note',
          status_change: 'Client status updated to Active',
          health_score_change: 'Health score improved to 85',
          issue_detected: 'Missing Director ID detected',
          issue_resolved: 'ABN verification completed',
        };

        const eventDescriptions: Record<string, string> = {
          document_upload: 'Tax return documents uploaded to client file',
          deadline_created: 'Quarterly BAS deadline added to calendar',
          deadline_completed: 'PAYG withholding lodgement submitted successfully',
          meeting: 'Discussed business performance and tax strategies',
          email: 'Sent year-end financial summary and tax estimates',
          phone_call: 'Consultation regarding tax deductions and planning',
          tax_lodgement: 'Individual tax return lodged with ATO',
          bas_lodgement: 'Business Activity Statement lodged electronically',
          note: 'Client requires quarterly compliance review',
          status_change: 'Client reactivated after completing documentation',
          health_score_change: 'File health improved after document updates',
          issue_detected: 'Director identification number not on file',
          issue_resolved: 'ABN verification completed and recorded',
        };

        events.push({
          id: `evt-${client.id}-${i}`,
          clientId: client.id,
          clientName: client.displayName,
          eventType,
          title: `${client.displayName}: ${eventTitles[eventType]}`,
          description: eventDescriptions[eventType],
          performedBy: ['Sarah Johnson', 'Michael Chen', 'Emma Wilson', 'System'][Math.floor(Math.random() * 4)],
          timestamp: timestamp.toISOString(),
        });
      }
    });

    // Apply filters
    let filtered = [...events];

    if (filters?.eventType && filters.eventType !== 'all') {
      filtered = filtered.filter((e: any) => e.eventType === filters.eventType);
    }

    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter((e: any) =>
        e.title.toLowerCase().includes(search) ||
        e.description?.toLowerCase().includes(search) ||
        e.clientName.toLowerCase().includes(search)
      );
    }

    // Sort by timestamp descending
    filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return {
      events: filtered,
      total: filtered.length,
    };
  },

  // Get regulatory changes and impacts
  async getRegulatoryChanges(filters?: { category?: string; severity?: string }): Promise<any> {
    await delay(400);

    const regulations = [
      {
        id: 'reg-1',
        title: 'Division 7A Deemed Dividend Rules - New ATO Guidance',
        category: 'tax',
        description: 'ATO has released updated guidance on Division 7A deemed dividends for private companies, clarifying treatment of unpaid present entitlements (UPEs) and sub-trust arrangements.',
        effectiveDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
        publishedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        source: 'ATO Practical Compliance Guideline',
        sourceUrl: 'https://www.ato.gov.au',
        severity: 'high',
        affectedClients: 18,
        estimatedRevenue: 54000,
        keyChanges: [
          'New safe harbor provisions for UPE arrangements',
          'Clarified treatment of sub-trust structures',
          'Updated documentation requirements for complying loans',
          'Extended transitional period for existing arrangements',
        ],
        requiredActions: [
          'Review all client UPE arrangements',
          'Update loan agreements to meet new requirements',
          'Prepare documentation for safe harbor compliance',
          'Communicate changes to affected clients',
        ],
      },
      {
        id: 'reg-2',
        title: 'SMSF Investment Strategy Review - Mandatory Annual Update',
        category: 'smsf',
        description: 'New requirement for all SMSFs to formally review and document investment strategies annually, with specific considerations for cryptocurrency and alternative assets.',
        effectiveDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
        publishedDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        source: 'ASIC Regulatory Guide Update',
        sourceUrl: 'https://www.asic.gov.au',
        severity: 'critical',
        affectedClients: 45,
        estimatedRevenue: 90000,
        keyChanges: [
          'Annual investment strategy review now mandatory (previously "when circumstances change")',
          'Must document consideration of cryptocurrency and digital assets',
          'Enhanced liquidity assessment requirements',
          'Trustee minute requirements strengthened',
        ],
        requiredActions: [
          'Schedule investment strategy reviews for all SMSF clients',
          'Prepare standardized review templates',
          'Assess cryptocurrency holdings across portfolio',
          'Update trustee minute procedures',
        ],
      },
      {
        id: 'reg-3',
        title: 'GST on Digital Products - Expanded Scope',
        category: 'gst',
        description: 'GST treatment extended to additional digital products and services, including NFTs, digital subscriptions, and online memberships.',
        effectiveDate: new Date(Date.now() + 120 * 24 * 60 * 60 * 1000).toISOString(),
        publishedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        source: 'ATO Taxation Determination',
        severity: 'medium',
        affectedClients: 12,
        estimatedRevenue: 24000,
        keyChanges: [
          'NFT sales now clearly within GST scope',
          'Streaming and subscription services clarified',
          'Online course and membership platforms included',
          'New record-keeping requirements for digital transactions',
        ],
        requiredActions: [
          'Identify clients with digital product sales',
          'Review BAS reporting for digital transactions',
          'Update GST registration thresholds assessment',
          'Implement new record-keeping systems',
        ],
      },
      {
        id: 'reg-4',
        title: 'Trust Vesting Date Extensions - Limited Time Window',
        category: 'trust',
        description: 'One-time opportunity to extend trust vesting dates for discretionary trusts established before 2000, subject to specific conditions.',
        effectiveDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        publishedDate: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
        source: 'State Legislation Amendment',
        severity: 'high',
        affectedClients: 8,
        estimatedRevenue: 32000,
        keyChanges: [
          'Vesting dates can be extended to 80 years from establishment',
          'Application must be lodged within 6 months',
          'Deed amendment and ASIC filing required',
          'Cannot extend if trust has already vested',
        ],
        requiredActions: [
          'Urgently identify trusts approaching vesting dates',
          'Prepare deed amendments for eligible trusts',
          'Calculate CGT and stamp duty implications',
          'Lodge applications before deadline',
        ],
      },
      {
        id: 'reg-5',
        title: 'Director ID - Expanded Verification Requirements',
        category: 'corporate',
        description: 'ASIC introduces enhanced verification for Director IDs, including mandatory address verification and photographic ID updates every 5 years.',
        effectiveDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString(),
        publishedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        source: 'ASIC Class Order',
        severity: 'medium',
        affectedClients: 35,
        estimatedRevenue: 42000,
        keyChanges: [
          'Address verification now required every 12 months',
          'Photographic ID must be updated every 5 years',
          'Failure to verify results in Director ID suspension',
          'Automated reminder system being implemented',
        ],
        requiredActions: [
          'Audit all client director IDs for compliance',
          'Set up verification tracking system',
          'Notify directors of upcoming verification deadlines',
          'Assist with verification process where needed',
        ],
      },
    ];

    let filtered = [...regulations];

    if (filters?.category && filters.category !== 'all') {
      filtered = filtered.filter((r: any) => r.category === filters.category);
    }

    if (filters?.severity && filters.severity !== 'all') {
      filtered = filtered.filter((r: any) => r.severity === filters.severity);
    }

    const stats = {
      totalRegulations: regulations.length,
      newThisMonth: regulations.filter((r: any) => {
        const publishedDate = new Date(r.publishedDate);
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        return publishedDate > thirtyDaysAgo;
      }).length,
      totalAffectedClients: filtered.reduce((sum: number, r: any) => sum + r.affectedClients, 0),
      criticalImpacts: filtered.filter((r: any) => r.severity === 'critical').length,
      highImpacts: filtered.filter((r: any) => r.severity === 'high').length,
      totalEstimatedRevenue: filtered.reduce((sum: number, r: any) => sum + r.estimatedRevenue, 0),
      unreviewedImpacts: Math.floor(filtered.reduce((sum: number, r: any) => sum + r.affectedClients, 0) * 0.6),
    };

    return {
      regulations: filtered,
      stats,
    };
  },

  // Get client impacts from regulations
  async getClientImpacts(filters?: { status?: string }): Promise<any> {
    await delay(400);

    const allClients = await this.listClients({ limit: 100 });
    const impacts: any[] = [];

    // Generate realistic client impacts
    allClients.clients.slice(0, 25).forEach((client: any, idx: number) => {
      const regulationMappings = [
        { regId: 'reg-1', title: 'Division 7A', severity: 'high', affected: client.clientType === 'company' },
        { regId: 'reg-2', title: 'SMSF Investment Strategy', severity: 'critical', affected: client.clientType === 'smsf' },
        { regId: 'reg-3', title: 'GST Digital Products', severity: 'medium', affected: Math.random() > 0.7 },
        { regId: 'reg-4', title: 'Trust Vesting', severity: 'high', affected: client.clientType === 'trust' },
        { regId: 'reg-5', title: 'Director ID', severity: 'medium', affected: client.clientType === 'company' },
      ];

      regulationMappings.forEach((mapping, mapIdx) => {
        if (mapping.affected) {
          impacts.push({
            clientId: client.id,
            clientName: client.displayName,
            clientType: client.clientType,
            regulationId: mapping.regId,
            regulationTitle: mapping.title,
            severity: mapping.severity,
            status: ['new', 'new', 'reviewed', 'actioned'][Math.floor(Math.random() * 4)],
            reasoning: `Based on analysis of ${client.displayName}'s file structure and entity type, this regulation will impact their ${mapping.title.toLowerCase()} arrangements.`,
            affectedAreas: ['Tax Returns', 'Compliance', 'Structuring'].slice(0, Math.floor(Math.random() * 2) + 1),
            recommendedActions: [
              'Schedule consultation to discuss implications',
              'Review current documentation and identify gaps',
              'Prepare compliance action plan with timeline',
            ],
            estimatedHours: Math.floor(Math.random() * 6) + 2,
            estimatedValue: (Math.floor(Math.random() * 6) + 2) * 250,
            identifiedAt: new Date(Date.now() - Math.random() * 20 * 24 * 60 * 60 * 1000).toISOString(),
          });
        }
      });
    });

    let filtered = [...impacts];

    if (filters?.status && filters.status !== 'all') {
      filtered = filtered.filter((i: any) => i.status === filters.status);
    }

    return {
      impacts: filtered,
      total: filtered.length,
    };
  },

  // Update impact status
  async updateImpactStatus(impactId: string, status: string): Promise<void> {
    await delay(200);
    console.log('Updating impact status:', impactId, status);
  },

  // Get document inconsistencies
  async getDocumentInconsistencies(filters?: { type?: string; severity?: string; resolved?: boolean; search?: string }): Promise<any> {
    await delay(400);

    const allClients = await this.listClients({ limit: 100 });
    const inconsistencies: any[] = [];

    const inconsistencyTemplates = [
      {
        type: 'missing_document',
        severity: 'critical',
        title: 'BAS Lodged Without Supporting GST Records',
        description: 'Q4 2023 BAS was lodged but no supporting GST calculation worksheets or tax invoices are in the file.',
        affectedDocuments: ['BAS Q4 2023.pdf'],
        expectedDocuments: ['GST Calculation Worksheet Q4 2023', 'Tax Invoice Register Q4 2023'],
        recommendation: 'Request missing GST supporting documentation from client and file immediately to support BAS lodgement.',
        autoFixable: false,
      },
      {
        type: 'data_mismatch',
        severity: 'high',
        title: 'Rental Income Declared Without Lease Agreement',
        description: 'Tax return shows $45,000 rental income but no lease agreement or property management statements are on file.',
        affectedDocuments: ['Tax Return 2023.pdf'],
        expectedDocuments: ['Residential Tenancy Agreement', 'Property Management Statements 2023'],
        recommendation: 'Obtain lease agreement and rental statements to substantiate income claim in case of ATO review.',
        autoFixable: false,
      },
      {
        type: 'cross_year_inconsistency',
        severity: 'medium',
        title: 'Depreciating Asset Not Carried Forward',
        description: 'Equipment asset ($25,000) claimed in 2022 tax return but not showing in 2023 depreciation schedule without sale documentation.',
        affectedDocuments: ['Tax Return 2022.pdf', 'Depreciation Schedule 2023.xlsx'],
        expectedDocuments: ['Asset Sale Documentation', 'Updated Depreciation Schedule'],
        recommendation: 'Verify if asset was sold, scrapped, or fully depreciated. Update records accordingly.',
        autoFixable: false,
      },
      {
        type: 'missing_supporting_doc',
        severity: 'high',
        title: 'SMSF Property Purchase Without Valuation',
        description: 'SMSF acquired commercial property for $850,000 but no independent valuation report is on file as required by SIS regulations.',
        affectedDocuments: ['SMSF Annual Return 2023.pdf', 'Property Settlement Statement.pdf'],
        expectedDocuments: ['Independent Property Valuation Report'],
        recommendation: 'Critical - Obtain retrospective independent valuation immediately. Required for audit and potential ATO review.',
        autoFixable: false,
      },
      {
        type: 'incomplete_set',
        severity: 'medium',
        title: 'Trust Distribution Missing Beneficiary Tax Statements',
        description: 'Trust tax return shows distributions to 4 beneficiaries but only 2 beneficiary tax statements are on file.',
        affectedDocuments: ['Trust Tax Return 2023.pdf', 'Beneficiary Statements (2 of 4)'],
        expectedDocuments: ['Beneficiary Tax Statement - John Doe', 'Beneficiary Tax Statement - Jane Doe'],
        recommendation: 'Generate and file missing beneficiary tax statements for all beneficiaries who received distributions.',
        autoFixable: true,
      },
      {
        type: 'expired_reference',
        severity: 'critical',
        title: 'Director Identification Expired',
        description: 'Directors passport used for ID verification expired 8 months ago. Current photo ID is required for ASIC compliance.',
        affectedDocuments: ['Director ID Verification - Passport (Expired).pdf'],
        expectedDocuments: ['Current Drivers License or Passport'],
        recommendation: 'Urgent - Request updated photo ID from director. Director ID may be suspended if not updated within 30 days.',
        autoFixable: false,
      },
      {
        type: 'missing_document',
        severity: 'high',
        title: 'Company Minutes Missing for Dividend Resolution',
        description: 'Company declared and paid $120,000 in dividends but no board minutes authorizing distribution are on file.',
        affectedDocuments: ['Dividend Payment Records.pdf'],
        expectedDocuments: ['Board Minutes - Dividend Declaration', 'Dividend Resolution'],
        recommendation: 'Prepare and backdate board minutes (if within reasonable timeframe) to properly document dividend authorization.',
        autoFixable: true,
      },
      {
        type: 'data_mismatch',
        severity: 'medium',
        title: 'FBT Return Shows Vehicle But No Logbook',
        description: 'FBT return claimed car FBT exemption using logbook method but no logbook is attached or referenced in file.',
        affectedDocuments: ['FBT Return 2023.pdf'],
        expectedDocuments: ['Motor Vehicle Logbook 2023'],
        recommendation: 'Obtain vehicle logbook from client. If not maintained, may need to amend FBT return using statutory method.',
        autoFixable: false,
      },
    ];

    allClients.clients.slice(0, 30).forEach((client: any) => {
      const numIssues = Math.floor(Math.random() * 3);
      for (let i = 0; i < numIssues; i++) {
        const template = inconsistencyTemplates[Math.floor(Math.random() * inconsistencyTemplates.length)];
        inconsistencies.push({
          id: `inc-${client.id}-${i}`,
          clientId: client.id,
          clientName: client.displayName,
          clientType: client.clientType,
          type: template.type,
          severity: template.severity,
          title: template.title,
          description: template.description,
          detectedAt: new Date(Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000).toISOString(),
          affectedDocuments: template.affectedDocuments,
          expectedDocuments: template.expectedDocuments,
          recommendation: template.recommendation,
          autoFixable: template.autoFixable,
          resolved: Math.random() > 0.7,
          resolvedAt: Math.random() > 0.7 ? new Date(Date.now() - Math.random() * 10 * 24 * 60 * 60 * 1000).toISOString() : undefined,
        });
      }
    });

    let filtered = [...inconsistencies];

    if (filters?.type && filters.type !== 'all') {
      filtered = filtered.filter((i: any) => i.type === filters.type);
    }

    if (filters?.severity && filters.severity !== 'all') {
      filtered = filtered.filter((i: any) => i.severity === filters.severity);
    }

    if (filters?.resolved !== undefined) {
      filtered = filtered.filter((i: any) => i.resolved === filters.resolved);
    }

    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter((i: any) =>
        i.clientName.toLowerCase().includes(search) ||
        i.title.toLowerCase().includes(search) ||
        i.description.toLowerCase().includes(search)
      );
    }

    const stats = {
      totalClients: allClients.total,
      clientsWithIssues: new Set(inconsistencies.map((i: any) => i.clientId)).size,
      totalInconsistencies: inconsistencies.length,
      criticalIssues: inconsistencies.filter((i: any) => i.severity === 'critical').length,
      highIssues: inconsistencies.filter((i: any) => i.severity === 'high').length,
      mediumIssues: inconsistencies.filter((i: any) => i.severity === 'medium').length,
      lowIssues: inconsistencies.filter((i: any) => i.severity === 'low').length,
      autoFixableIssues: inconsistencies.filter((i: any) => i.autoFixable).length,
      resolvedIssues: inconsistencies.filter((i: any) => i.resolved).length,
    };

    return {
      inconsistencies: filtered,
      stats,
    };
  },

  // Resolve inconsistency
  async resolveInconsistency(id: string): Promise<void> {
    await delay(300);
    console.log('Resolving inconsistency:', id);
  },

  // Auto-fix inconsistency
  async autoFixInconsistency(id: string): Promise<void> {
    await delay(500);
    console.log('Auto-fixing inconsistency:', id);
  },

  // Chat with client files
  async chatWithClientFiles(
    request: import('@/types/client').ClientFileChatRequest
  ): Promise<import('@/types/client').ClientFileChatResponse> {
    await delay(800); // Simulate AI processing

    const { message, clientId } = request;
    const lowerMessage = message.toLowerCase();

    // Get client details for contextual responses
    const client = mockClients.find((c) => c.id === clientId);
    if (!client) {
      throw new Error('Client not found');
    }

    // Simulated knowledge base for different questions
    let responseContent = '';
    let citations: import('@/types/client').DocumentCitation[] = [];

    // ABN/ACN queries
    if (lowerMessage.includes('abn')) {
      responseContent = client.abn
        ? `The ABN for ${client.displayName} is ${client.abn}.`
        : `I couldn't find an ABN for ${client.displayName} in the client files.`;
      if (client.abn) {
        citations = [
          {
            documentId: 'doc-1',
            documentName: 'Client Details Form.pdf',
            pageNumber: 1,
            excerpt: `ABN: ${client.abn}`,
            relevanceScore: 0.98,
          },
          {
            documentId: 'doc-2',
            documentName: 'Business Registration Certificate.pdf',
            pageNumber: 1,
            excerpt: `Australian Business Number ${client.abn} was registered on...`,
            relevanceScore: 0.95,
          },
        ];
      }
    }
    // ACN queries
    else if (lowerMessage.includes('acn')) {
      responseContent = client.acn
        ? `The ACN for ${client.displayName} is ${client.acn}.`
        : `This client doesn't have an ACN on file. ACN is only applicable for companies.`;
      if (client.acn) {
        citations = [
          {
            documentId: 'doc-3',
            documentName: 'ASIC Company Extract.pdf',
            pageNumber: 1,
            excerpt: `Australian Company Number (ACN): ${client.acn}`,
            relevanceScore: 0.99,
          },
        ];
      }
    }
    // TFN queries
    else if (lowerMessage.includes('tfn') || lowerMessage.includes('tax file')) {
      responseContent = `I found the TFN in the confidential client records. For security reasons, I can show you where it's located but won't display the full number here.`;
      citations = [
        {
          documentId: 'doc-4',
          documentName: 'Tax File Number Declaration.pdf',
          pageNumber: 1,
          excerpt: 'Tax File Number: [REDACTED FOR SECURITY]',
          relevanceScore: 0.97,
        },
      ];
    }
    // BAS queries
    else if (lowerMessage.includes('bas')) {
      const filingDate = '21 Apr 2024';
      const gstAmount = client.clientType === 'company' ? '$12,450' : '$3,200';
      responseContent = `The latest BAS for ${client.displayName} was lodged on ${filingDate}. The GST payable was ${gstAmount}.`;
      citations = [
        {
          documentId: 'doc-5',
          documentName: 'BAS - Mar 2024 Quarter.pdf',
          pageNumber: 1,
          excerpt: `GST on sales: ${gstAmount} | GST on purchases: $8,200 | Net GST payable: ${gstAmount}`,
          relevanceScore: 0.96,
        },
        {
          documentId: 'doc-6',
          documentName: 'BAS Lodgement Receipt - Mar 2024.pdf',
          pageNumber: 1,
          excerpt: `Successfully lodged on ${filingDate} for period ending 31 March 2024`,
          relevanceScore: 0.93,
        },
      ];
    }
    // Income queries
    else if (lowerMessage.includes('income') || lowerMessage.includes('revenue')) {
      const year = lowerMessage.match(/\d{4}/) ? lowerMessage.match(/\d{4}/)?.[0] : '2023';
      const income = client.clientType === 'company' ? '$2,450,000' : '$180,000';
      responseContent = `For the ${year} financial year, ${client.displayName} reported total income of ${income}.`;
      citations = [
        {
          documentId: 'doc-7',
          documentName: `Tax Return - FY${year}.pdf`,
          pageNumber: 2,
          excerpt: `Total Income: ${income} | Total Deductions: $45,000 | Taxable Income: ${client.clientType === 'company' ? '$2,405,000' : '$135,000'}`,
          relevanceScore: 0.98,
        },
        {
          documentId: 'doc-8',
          documentName: `Financial Statements - ${year}.pdf`,
          pageNumber: 3,
          excerpt: `Revenue from ordinary activities: ${income}`,
          relevanceScore: 0.92,
        },
      ];
    }
    // Address queries
    else if (lowerMessage.includes('address')) {
      responseContent = client.address
        ? `The registered address for ${client.displayName} is: ${client.address}`
        : `I couldn't find a registered address in the client files.`;
      if (client.address) {
        citations = [
          {
            documentId: 'doc-9',
            documentName: 'Client Information Sheet.pdf',
            pageNumber: 1,
            excerpt: `Registered Address: ${client.address}`,
            relevanceScore: 0.96,
          },
        ];
      }
    }
    // Contact queries
    else if (lowerMessage.includes('email') || lowerMessage.includes('phone') || lowerMessage.includes('contact')) {
      const contactInfo = [];
      if (client.email) contactInfo.push(`Email: ${client.email}`);
      if (client.phone) contactInfo.push(`Phone: ${client.phone}`);

      responseContent = contactInfo.length > 0
        ? `Contact details for ${client.displayName}:\n${contactInfo.join('\n')}`
        : `I couldn't find contact details in the client files.`;

      if (contactInfo.length > 0) {
        citations = [
          {
            documentId: 'doc-10',
            documentName: 'Client Contact Details.pdf',
            pageNumber: 1,
            excerpt: contactInfo.join(' | '),
            relevanceScore: 0.94,
          },
        ];
      }
    }
    // Industry queries
    else if (lowerMessage.includes('industry') || lowerMessage.includes('business type')) {
      responseContent = client.industry
        ? `${client.displayName} operates in the ${client.industry} industry.`
        : `I couldn't find industry classification in the client files.`;
      if (client.industry) {
        citations = [
          {
            documentId: 'doc-11',
            documentName: 'Business Activity Statement.pdf',
            pageNumber: 1,
            excerpt: `Industry Classification: ${client.industry}`,
            relevanceScore: 0.91,
          },
        ];
      }
    }
    // Document count queries
    else if (lowerMessage.includes('how many document') || lowerMessage.includes('number of file')) {
      responseContent = `${client.displayName} currently has ${client.totalDocuments} documents on file, with ${client.indexedDocuments} indexed for AI search. The total storage used is ${(client.totalSizeBytes / (1024 * 1024)).toFixed(2)} MB.`;
      citations = [
        {
          documentId: 'system',
          documentName: 'System File Index',
          excerpt: `Total documents: ${client.totalDocuments} | Indexed: ${client.indexedDocuments} | Storage: ${(client.totalSizeBytes / (1024 * 1024)).toFixed(2)} MB`,
          relevanceScore: 1.0,
        },
      ];
    }
    // Deadline queries
    else if (lowerMessage.includes('deadline') || lowerMessage.includes('due date')) {
      responseContent = `Based on the client's latest deadlines:\n• BAS Quarter 2: Due 28 July 2024\n• Income Tax Return FY2024: Due 31 October 2024\n• PAYG Summary: Due 14 August 2024`;
      citations = [
        {
          documentId: 'doc-12',
          documentName: 'Compliance Calendar 2024.pdf',
          pageNumber: 1,
          excerpt: 'Upcoming lodgements: BAS Q2 (28/07), Tax Return (31/10), PAYG (14/08)',
          relevanceScore: 0.95,
        },
      ];
    }
    // Generic fallback
    else {
      responseContent = `I searched through ${client.indexedDocuments} indexed documents for "${message}" but couldn't find specific information. Try asking about:\n• ABN, ACN, or TFN\n• BAS filings or tax returns\n• Income or revenue\n• Contact details or address\n• Upcoming deadlines`;
      citations = [];
    }

    const assistantMessage: import('@/types/client').ChatMessage = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: responseContent,
      timestamp: new Date().toISOString(),
      citations: citations.length > 0 ? citations : undefined,
    };

    return {
      message: assistantMessage,
      sources: citations,
    };
  },
};
